/* eslint-disable no-restricted-imports */
/*
 * @author        zach.william@zumper.com (Zach William)
 * @copyright     Copyright (c) 2023, Zumper
 * @description   BackNavButton.tsx
 */

import { ChevronLeft } from '@zcode-ui/icons'
import { cn } from '@zcode-ui/shadz-ui'
import { selectEntity } from '@zcode-ui/web-zumper-global-state'
import { LinkOrButton } from '@zcode-ui/web-zumper-ui'
import { AutocompleteResult } from '@zcode/bizos'
import { localStorageSingleton } from '@zcode/nr-utils'
import Filter from '@zcode/nr/es/shared/bizos/filter'
import type { FiltersState } from '@zcode/state-type'
import { toSlug } from '@zcode/utils'
import type { Location, LocationDescriptor } from 'history'
import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'

export interface BackNavButtonProps {
  city: string
  cityUrl: string
  className?: string
  getRootRelativeUrlFromFiltersAndLocation: (args: {
    bubbleId?: string | undefined
    currentUrl: string
    filters: FiltersState
    locationUrlQueries?: string[] | undefined
    omitBox?: boolean | undefined
  }) => string
  handleBackBtnClick?: (result: AutocompleteResult) => void
  keepBetaParam: <ToLocationState>(
    to: LocationDescriptor<ToLocationState>
  ) => (location: Location<unknown>) => LocationDescriptor<ToLocationState>
  onClick?: () => void
  preloadRoutes: (componentNames: string[]) => void
  Routes: {
    CURRENT_SEARCH_ROUTE: string
  }
}

export const BackNavButton = ({
  city,
  cityUrl,
  className,
  getRootRelativeUrlFromFiltersAndLocation,
  handleBackBtnClick,
  keepBetaParam,
  onClick: onClickProp,
  preloadRoutes,
  Routes,
}: BackNavButtonProps) => {
  const onMouseEnter = useCallback(() => {
    preloadRoutes([Routes.CURRENT_SEARCH_ROUTE])
  }, [Routes, preloadRoutes])

  const entity = useSelector(selectEntity)
  const entityUrl =
    entity?.cityState || (entity?.city && entity?.state && toSlug(entity.city, entity.state))

  const url = useMemo(() => {
    const fallbackUrl = cityUrl || '/'

    const lsFilters = localStorageSingleton.get('mapListFilters')

    if (!lsFilters) {
      return fallbackUrl
    }

    const filters = Filter.fromJS(lsFilters)
    const { url } = filters
    const currentUrl = url || fallbackUrl

    if (currentUrl === '/') {
      return currentUrl
    }

    const backUrl = getRootRelativeUrlFromFiltersAndLocation({
      filters,
      currentUrl,
    })

    return backUrl || fallbackUrl
  }, [cityUrl, getRootRelativeUrlFromFiltersAndLocation])

  const onClick = useCallback(
    async (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
      onClickProp?.()
      const fromMultiUnitAddressUnitsTable =
        window.opener?.location.pathname === window.location.pathname &&
        window.location.search.includes('listing_id')
      try {
        if (
          // if the user landed on this detail page by opening a new window from the maplist page,
          // and that maplist page is still open and they want to go back to it, let's "take them
          // back there" by just closing this superfluous window
          // https://github.com/microsoft/TypeScript-DOM-lib-generator/issues/1418
          // window.opener is typed as any 😔
          window.opener?.location.pathname === new URL(url, window.location.href).pathname ||
          // If the user got here by clicking on one of the units in the multi-unit address page
          // let's send the users back to the original page instead of the XFR page
          fromMultiUnitAddressUnitsTable
        ) {
          e.preventDefault()
          window.close()
        } else if (entityUrl && handleBackBtnClick) {
          const detailLocationObj = new AutocompleteResult({
            city: entity.city,
            country: entity.country,
            is_derived_from_entity: true,
            lat: entity.lat,
            lng: entity.lng,
            name: entity.city,
            record_type: 'city',
            state: entity.state,
            url: entityUrl,
          } as const)

          e.preventDefault()
          // this will call nzr's navigateToAutocompleteResult, which (I think) will handle
          // ZR correctly
          handleBackBtnClick(detailLocationObj)
        }
      } catch {}
    },
    [entity, entityUrl, handleBackBtnClick, onClickProp, url]
  )

  return (
    <LinkOrButton
      aria-label={city ? `see housing in ${city}` : 'see more housing'}
      data-testid="back-nav-button"
      className={cn(
        'flex items-center justify-center bg-gray-0 rounded-full h-8 w-8 lg:h-5 lg:w-5',
        className
      )}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      to={keepBetaParam(url)}
    >
      <ChevronLeft fill="#454442" size={12} />
    </LinkOrButton>
  )
}
